import { MeasurementSystem } from '../../model/enums/measurement-system';

export interface UnitConversion {
  // [id: string] where string is of type MeasurementSystem
  [id: string]: {
    [id: string]: {
      name: string;
      coefficient: number;
    };
  };
}
export interface CustomUnitConversion {
  [id: string]: {
    name: string;
    factors?: UniqueConversionUnit[];
    denominators?: UniqueConversionUnit[];
  };
}

export interface ConversionData {
  unit: string;
  factors?: UniqueConversionUnit[];
  denominators?: UniqueConversionUnit[];
}

export interface EnergyUnitConversion {
  [originUnit: string]: {
    [EnergyUnit.KWH]?: ConversionData;
    [EnergyUnit.MMBTU]?: ConversionData;
  };
}

export enum EnergyUnit {
  KWH = 'KWH',
  MMBTU = 'MMBTU',
}

export enum UniqueConversionUnit {
  KM = 'km',
  L = 'l',
  M3 = 'm3',
  M2 = 'm2',
  KG = 'kg',
  T = 'tonne',
  KWH = 'kwh',
}

export const customUnitConversions: CustomUnitConversion = {
  g_co2e_m2: { name: 'g_co2e_sqft', denominators: [UniqueConversionUnit.M2] },
  g_co2e_m3: { name: 'g_co2e_gal', denominators: [UniqueConversionUnit.M3] },
  g_co2e_pkm: { name: 'g_co2e_pmile', denominators: [UniqueConversionUnit.KM] },
  g_co2e_tonne: { name: 'g_co2e_shortton', denominators: [UniqueConversionUnit.T] },
  g_co2e_tonne_km: { name: 'g_co2e_shortton_mile', denominators: [UniqueConversionUnit.T, UniqueConversionUnit.KM] },
  ideal_vehicle_km: { name: 'ideal_vehicle_mile', factors: [UniqueConversionUnit.KM] },
  kg: { name: 'lbs', factors: [UniqueConversionUnit.KG] },
  km: { name: 'mile', factors: [UniqueConversionUnit.KM] },
  km_commute: { name: 'mile_commute', factors: [UniqueConversionUnit.KM] },
  kwh_person_km: { name: 'kwh_pmile', denominators: [UniqueConversionUnit.KM] },
  kwh_vehicle_km: { name: 'kwh_vehicle_mile', denominators: [UniqueConversionUnit.KM] },
  kwh_km: { name: 'kwh_mile', denominators: [UniqueConversionUnit.KM] },
  kwh_m2: { name: 'kwh_sqft', denominators: [UniqueConversionUnit.M2] },
  kwh_m3: { name: 'kwh_gal', denominators: [UniqueConversionUnit.M3] },
  kwh_tonne: { name: 'kwh_shortton', denominators: [UniqueConversionUnit.T] },
  kwh_tonne_km: { name: 'kwh_shortton_mile', denominators: [UniqueConversionUnit.T, UniqueConversionUnit.KM] },
  l: { name: 'gal', factors: [UniqueConversionUnit.L] },
  g_vkm: { name: 'g_vehicle_mile', denominators: [UniqueConversionUnit.KM] },
  m2: { name: 'sqft', factors: [UniqueConversionUnit.M2] },
  m2_percapita: { name: 'sqft_percapita', factors: [UniqueConversionUnit.M2] },
  m3: { name: 'gal', factors: [UniqueConversionUnit.M3] },
  m3_percapita: { name: 'gal_percapita', factors: [UniqueConversionUnit.M3] },
  money_l: { name: 'money_gal', denominators: [UniqueConversionUnit.L] },
  money_vehicle_km: { name: 'money_vehicle_mile', denominators: [UniqueConversionUnit.KM] },
  person_km: { name: 'pmile', factors: [UniqueConversionUnit.KM] },
  person_km_percapita: { name: 'pmile_percapita', factors: [UniqueConversionUnit.KM] },
  pkm: { name: 'pmile', factors: [UniqueConversionUnit.KM] },
  tonne: { name: 'shortton', factors: [UniqueConversionUnit.T] },
  tonne_km: { name: 'shortton_mile', factors: [UniqueConversionUnit.T, UniqueConversionUnit.KM] },
  tonne_km_percapita: { name: 'shortton_mile_percapita', factors: [UniqueConversionUnit.T, UniqueConversionUnit.KM] },
  tonne_kwh: { name: 'shortton_kwh', factors: [UniqueConversionUnit.T] },
  tonne_percapita: { name: 'shortton_percapita', factors: [UniqueConversionUnit.T] },
  vehicle_km: { name: 'vehicle_mile', factors: [UniqueConversionUnit.KM] },
  vehicle_km_percapita: { name: 'vehicle_mile_percapita', factors: [UniqueConversionUnit.KM] },
};

export const energyUnitConversionsMetricToUSCS: EnergyUnitConversion = {
  g_co2e_kwh: { [EnergyUnit.MMBTU]: { unit: 'g_co2e_mmbtu', denominators: [UniqueConversionUnit.KWH] } },
  g_kwh: { [EnergyUnit.MMBTU]: { unit: 'g_mmbtu', denominators: [UniqueConversionUnit.KWH] } },
  kw_kwh: { [EnergyUnit.MMBTU]: { unit: 'kw_mmbtu', denominators: [UniqueConversionUnit.KWH] } },
  kwh: { [EnergyUnit.MMBTU]: { unit: 'mmbtu', factors: [UniqueConversionUnit.KWH] } },
  kwh_h: { [EnergyUnit.MMBTU]: { unit: 'mmbtu_h', factors: [UniqueConversionUnit.KWH] } },
  kwh_kwh: {
    [EnergyUnit.MMBTU]: {
      unit: 'mmbtu_mmbtu',
      factors: [UniqueConversionUnit.KWH],
      denominators: [UniqueConversionUnit.KWH],
    },
  },
  kwh_m2: {
    [EnergyUnit.KWH]: { unit: 'kwh_sqft', denominators: [UniqueConversionUnit.M2] },
    [EnergyUnit.MMBTU]: {
      unit: 'mmbtu_sqft',
      factors: [UniqueConversionUnit.KWH],
      denominators: [UniqueConversionUnit.M2],
    },
  },
  kwh_percapita: { [EnergyUnit.MMBTU]: { unit: 'mmbtu_percapita', factors: [UniqueConversionUnit.KWH] } },
  kwh_person_km: {
    [EnergyUnit.KWH]: { unit: 'kwh_pmile', denominators: [UniqueConversionUnit.KM] },
    [EnergyUnit.MMBTU]: {
      unit: 'mmbtu_pmile',
      factors: [UniqueConversionUnit.KWH],
      denominators: [UniqueConversionUnit.KM],
    },
  },
  kwh_tonne: {
    [EnergyUnit.KWH]: { unit: 'kwh_shortton', denominators: [UniqueConversionUnit.T] },
    [EnergyUnit.MMBTU]: {
      unit: 'mmbtu_shortton',
      factors: [UniqueConversionUnit.KWH],
      denominators: [UniqueConversionUnit.T],
    },
  },
  kwh_tonne_km: {
    [EnergyUnit.KWH]: { unit: 'kwh_shortton_mile', denominators: [UniqueConversionUnit.T, UniqueConversionUnit.KM] },
    [EnergyUnit.MMBTU]: {
      unit: 'mmbtu_shortton_mile',
      factors: [UniqueConversionUnit.KWH],
      denominators: [UniqueConversionUnit.T, UniqueConversionUnit.KM],
    },
  },
  kwh_vehicle_km: {
    [EnergyUnit.KWH]: { unit: 'kwh_vehicle_mile', denominators: [UniqueConversionUnit.KM] },
    [EnergyUnit.MMBTU]: {
      unit: 'mmbtu_vehicle_mile',
      factors: [UniqueConversionUnit.KWH],
      denominators: [UniqueConversionUnit.KM],
    },
  },
  kwp_kwh: { [EnergyUnit.MMBTU]: { unit: 'kwp_mmbtu', denominators: [UniqueConversionUnit.KWH] } },
  money_kwh: { [EnergyUnit.MMBTU]: { unit: 'money_mmbtu', denominators: [UniqueConversionUnit.KWH] } },
  tonne_kwh: {
    [EnergyUnit.KWH]: { unit: 'shortton_kwh', factors: [UniqueConversionUnit.T] },
    [EnergyUnit.MMBTU]: {
      unit: 'shortton_mmbtu',
      factors: [UniqueConversionUnit.T],
      denominators: [UniqueConversionUnit.KWH],
    },
  },
};

export const uniqueUnitConversions: UnitConversion = {
  [MeasurementSystem.USCustomaryUnits]: {
    [UniqueConversionUnit.KM]: { name: 'mile', coefficient: 0.62137119223733 },
    [UniqueConversionUnit.L]: { name: 'gal', coefficient: 0.26417205235815 },
    [UniqueConversionUnit.M3]: { name: 'gal', coefficient: 264.17205235815 },
    [UniqueConversionUnit.M2]: { name: 'sqft', coefficient: 10.76391042 },
    [UniqueConversionUnit.KG]: { name: 'lbs', coefficient: 2.2046226218 },
    [UniqueConversionUnit.T]: { name: 'shortton', coefficient: 1.1023113109244 },
    [UniqueConversionUnit.KWH]: { name: 'mmbtu', coefficient: 0.003413 },
  },
};
